/** Blends two theme hexes. A tint has no edge against its own fill, so strokes
 *  and particles take a step toward the magenta to stay legible in the light
 *  theme without becoming a second accent. Also spaces the streams by rank. */
export function mixHex(a: string, b: string, amountOfB: number): string {
  const parse = (hex: string) => {
    const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
    return m ? [1, 2, 3].map((i) => Number.parseInt(m[i]!, 16)) : null;
  };
  const ca = parse(a);
  const cb = parse(b);
  if (!ca || !cb) return a;
  const channel = (i: number) =>
    Math.round(ca[i]! + (cb[i]! - ca[i]!) * amountOfB)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

export const CURVE_SAMPLES = 128;

/** Catmull-Rom position at parameter u within segment i, for one axis. */
function catmull(p0: number, p1: number, p2: number, p3: number, u: number) {
  const u2 = u * u;
  const u3 = u2 * u;
  return (
    0.5 *
    (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2 + (-p0 + 3 * p1 - 3 * p2 + p3) * u3)
  );
}

/**
 * Resamples a series of per-period values into a smooth 0..1 profile. Both
 * layers of a stream — the 2D ribbon and the WebGL particles inside it — read
 * this same array, so they can never trace two slightly different shapes.
 *
 * `max` is passed in rather than taken per-series: the streams share one scale,
 * which is the whole point of stacking them.
 */
export function sampleProfile(values: number[], max: number): Float32Array {
  const out = new Float32Array(CURVE_SAMPLES);
  if (values.length === 0 || max <= 0) return out;
  const at = (i: number) => values[Math.max(0, Math.min(values.length - 1, i))]!;
  for (let s = 0; s < CURVE_SAMPLES; s += 1) {
    // A week is a band, not a point: the value belongs at the centre of its
    // band, so n weeks divide the width into n columns rather than n-1. Getting
    // this wrong renders one column too few and shifts every value half a week.
    const pos = (s / (CURVE_SAMPLES - 1)) * values.length - 0.5;
    const i = Math.floor(pos);
    const value = catmull(at(i - 1), at(i), at(i + 1), at(i + 2), pos - i);
    out[s] = Math.max(0, Math.min(1, value / max));
  }
  return out;
}
